/* ============================================================
   product.js — the product page behaviour layer.

   Loaded only on product templates (layout/theme.liquid).

   Four elements:

   1. <nmp-variants> — intercepts a click on a size or colour and swaps the buy
      panel in place instead of navigating. The option values are real links
      (snippets/variant-picker.liquid) so with this file absent every one of
      them still works by page load; all this adds is the absence of a reload.

      The replacement markup is fetched from Liquid rather than assembled here.
      That is the whole design: the price format, the low-stock line, the CTA
      wording, the availability reaction and the notify-me form's product and
      variant properties are all decided by Liquid, so there is no second copy
      of that logic in JavaScript to fall out of step — and no variant blob in
      the page, which would be wrong past 250 variants anyway.

   2. <nmp-gallery> — the dots and counter, the desktop carousel's arrows and
      arrow keys, and the zoom dialog. The track scrolls natively, so swiping
      works before and without this file, and every control stays CSS-hidden
      until this one sets data-ready. snippets/product-gallery.liquid holds the
      account for all of it — read it before changing anything below.

   3. <nmp-sticky-buy> — shows the bottom bar on a phone once the real button
      has scrolled away. Its button is a submit for the product form via the
      form attribute, so the bar needs no script to actually add to the bag.

   There is no size-guide element. It was a bare <nmp-overlay> subclass and
   was removed 2026-08-10 with the whole feature — the size chart is now
   uploaded as one of the product's images, so the gallery and its zoom carry
   it. sections/main-product.liquid's header has the account.

   Zoom deliberately uses a native <dialog> rather than <nmp-overlay>: it gets
   the focus trap, the Esc key and an inert background from the browser, which
   is less code and a stronger guarantee than we would write. The one thing
   <dialog> does not do is stop the page behind it scrolling, so that is added
   here by hand.

   Last updated: 2026-09-04
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  var DESKTOP = '(min-width: 1024px)';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------- variants */

  class NmpVariants extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sectionId = this.getAttribute('data-section-id');
      this.addEventListener('click', this._onClick.bind(this));
    }

    disconnectedCallback() {
      if (this._controller) this._controller.abort();
    }

    _onClick(event) {
      var link = event.target.closest('a[data-option-value]');
      if (!link) return;
      /* Leave modified clicks alone — someone opening a size in a new tab
         means it, and that link is a real address. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this._select(link.getAttribute('href'), link.getAttribute('data-option-value'));
    }

    _select(href, valueId) {
      var self = this;
      var panel = document.querySelector('[data-buy-panel]');
      if (!panel) {
        window.location.href = href;
        return;
      }

      if (this._controller) this._controller.abort();
      this._controller = new AbortController();

      panel.setAttribute('aria-busy', 'true');

      /* Every option-value href already carries a query string — either
         ?variant= for a real variant or ?option_values= for a combination that
         has none — so the separator is always an ampersand. */
      window
        .fetch(href + '&section_id=' + encodeURIComponent(this.sectionId), {
          signal: this._controller.signal
        })
        .then(function (response) {
          if (!response.ok) throw new Error('variant');
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var incoming = doc.querySelector('[data-buy-panel]');
          if (!incoming) throw new Error('panel');

          /* Section CSS is subsetted per page render tree, so inserting PART
             of a response means lifting its own style[data-section-stylesheet]
             out by hand. Not theoretical: on a PDP loaded at an in-stock
             variant, klaviyo-form.liquid is not in the render tree, so picking
             a sold-out size brought the notify-me form back naked. cart.js,
             collection.js and search.js always did this; product.js did not,
             from Phase 2. docs/01 §Known platform constraints. */
          if (window.NmpAdoptSectionStyles) window.NmpAdoptSectionStyles(doc, self.sectionId);

          panel.innerHTML = incoming.innerHTML;
          panel.removeAttribute('aria-busy');

          /* Say what changed. The swap replaces the price, the stock line and
             the call to action in one go, and until 2026-08-09 announced none
             of it — a screen-reader user picked a size and got silence, with no
             way to learn the piece was now sold out.

             The sentence is composed in Liquid ([data-variant-announce] inside
             the panel) and copied into the persistent region OUTSIDE it. It has
             to be done in this order: the region cannot live in the panel,
             because a live region destroyed and recreated by a swap never
             announces. Same shape as collection.js's
             [data-facet-count] -> [data-facet-status]. */
          var says = panel.querySelector('[data-variant-announce]');
          var region = document.querySelector('[data-variant-status]');
          if (says && region) region.textContent = says.textContent;

          /* The address is the variant's own, so a reload, a share or a
             back-forward all land on the same size. Replace rather than push:
             stepping back through every size someone tried is not history. */
          window.history.replaceState({ nmpVariant: true }, '', href);

          self._refocus(valueId);

          /* The gallery sits outside the buy panel and is deliberately not
             re-rendered, so which photo the new variant belongs to is read off
             the response instead. */
          var gallery = doc.querySelector('nmp-gallery');
          document.dispatchEvent(
            new CustomEvent('nmp:variant:change', {
              detail: { mediaId: gallery ? gallery.getAttribute('data-active-media') : null }
            })
          );
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          /* The link was always a real link. Use it. */
          window.location.href = href;
        });
    }

    /* The element that was clicked has just been replaced, so focus has fallen
       to the body. Put it back on the same value in the new markup. */
    _refocus(valueId) {
      if (!valueId) return;
      /* CSS.escape, unquoted — the fix collection.js:296 documents. Numeric
         ids today, but that file already shipped the version that mattered:
         a size written 5" threw mid-swap and the catch reloaded the page. */
      var next = document.querySelector('a[data-option-value=' + CSS.escape(valueId) + ']');
      if (next) next.focus({ preventScroll: true });
    }

  }

  /* ---------------------------------------------------------- gallery */

  class NmpGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this._ready = true;

      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-item]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-dot]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.desktop = window.matchMedia(DESKTOP);

      /* Emitted in the carousel shape only, so a null prev IS the shape test. */
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');

      this._onVariant = this._variant.bind(this);
      document.addEventListener('nmp:variant:change', this._onVariant);

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('keydown', this._onKey.bind(this));
      this._watch();
      this._wireDialog();

      /* The arrows and the desktop counter are hidden until this lands. */
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      document.removeEventListener('nmp:variant:change', this._onVariant);
      if (this._observer) this._observer.disconnect();
      this._releaseLock();
    }

    /* Which item is on screen drives the dots and the counter. Watching the
       track rather than listening to scroll keeps this off the main thread. */
    _watch() {
      if (!this.items.length || !('IntersectionObserver' in window)) return;
      var self = this;
      this._observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.intersectionRatio < 0.6) return;
            self._setActive(self.items.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.items.forEach(function (item) {
        self._observer.observe(item);
      });
    }

    _setActive(index) {
      if (index < 0 || index === this._active) return;
      this._active = index;
      this.dots.forEach(function (dot, i) {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counter) {
        /* The template wrote "1 / 5"; only the first number ever changes, so
           the separator and the total are read straight back off it rather
           than duplicating the translation here. */
        var parts = this.counter.textContent.split('/');
        if (parts.length === 2) this.counter.textContent = index + 1 + ' /' + parts[1];
      }
      this._edges();
    }

    _onClick(event) {
      var step = event.target.closest('[data-gallery-prev],[data-gallery-next]');
      if (step) {
        this._step(step.hasAttribute('data-gallery-next') ? 1 : -1);
        return;
      }
      var dot = event.target.closest('[data-gallery-dot]');
      if (dot) {
        this._scrollTo(this.items[parseInt(dot.getAttribute('data-index'), 10)]);
        return;
      }
      var zoom = event.target.closest('[data-gallery-zoom]');
      if (zoom) this._openZoom(zoom);
    }

    _scrollTo(item) {
      if (!item) return;
      this.track.scrollTo({
        left: item.offsetLeft - this.track.offsetLeft,
        behavior: reducedMotion() ? 'auto' : 'smooth'
      });
    }

    /* Optimistic: the counter and the edges must not lag a smooth scroll, and
       a fast second click must not read a stale index. The observer confirms. */
    _step(delta) {
      var next = (this._active || 0) + delta;
      if (next < 0 || next >= this.items.length) return;
      this._setActive(next);
      this._scrollTo(this.items[next]);
    }

    /* Null prev = a shape with no arrows. aria-disabled, not disabled, so the
       button stays focusable at the ends — snippet doc, Arrows. */
    _edges() {
      if (!this.prev) return;
      var i = this._active || 0;
      this.prev.setAttribute('aria-disabled', i === 0);
      this.next.setAttribute('aria-disabled', i >= this.items.length - 1);
    }

    /* On the gallery, never the document, so the buy column — a sibling — keeps
       its own arrow keys. The dialog IS a descendant, hence the one guard.
       Snippet doc, Keyboard. */
    _onKey(event) {
      if (!this.prev) return;
      if (this.dialog && this.dialog.open) return;
      if (event.key === 'ArrowRight') this._step(1);
      else if (event.key === 'ArrowLeft') this._step(-1);
      else return;
      event.preventDefault();
    }

    /* The phone always follows the colour; desktop only in the carousel, where
       the move is inside the track, not the page. Read live — snippet doc,
       Following the variant. */
    _variant(event) {
      if (this.desktop.matches && this.getAttribute('data-layout') !== 'carousel') return;
      var mediaId = event.detail && event.detail.mediaId;
      if (!mediaId) return;
      var item = this.querySelector('[data-media-id=' + CSS.escape(mediaId) + ']');
      if (item) this._scrollTo(item);
    }

    _wireDialog() {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      var self = this;

      this.dialog.addEventListener('click', function (event) {
        /* A click that lands on the dialog itself is a click on the backdrop —
           the image and the close button are children and stop here. */
        if (event.target === self.dialog) self.dialog.close();
        if (event.target.closest('[data-gallery-dialog-close]')) self.dialog.close();
      });

      this.dialog.addEventListener('close', function () {
        self._releaseLock();
        if (self._opener) {
          self._opener.focus({ preventScroll: true });
          self._opener = null;
        }
      });
    }

    _openZoom(trigger) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      var image = this.dialog.querySelector('[data-gallery-dialog-image]');
      if (!image) return;

      /* Dimensions before source, so the box is the right shape before the
         file arrives and nothing inside the dialog jumps. */
      image.width = trigger.getAttribute('data-zoom-w');
      image.height = trigger.getAttribute('data-zoom-h');
      image.alt = trigger.getAttribute('data-zoom-alt') || '';
      image.src = trigger.getAttribute('data-zoom-src');

      this._opener = trigger;
      this.dialog.showModal();

      /* showModal makes the background inert but does not stop it scrolling.
         The lock is reference counted in nmp.js so that closing an overlay
         while this dialog is open does not unlock the page underneath it. */
      if (!this._locked) {
        this._locked = true;
        if (window.NmpScrollLock) window.NmpScrollLock.lock();
        else root.setAttribute('data-nmp-locked', '');
      }
    }

    /* Two things end a zoom: the dialog's close event, and this element
       being torn out from under an open dialog. Removing an open modal
       <dialog> does NOT fire close, so a re-render during zoom left
       data-nmp-locked set and the page unscrollable for the session. */
    _releaseLock() {
      if (!this._locked) return;
      this._locked = false;
      if (window.NmpScrollLock) window.NmpScrollLock.unlock();
      else root.removeAttribute('data-nmp-locked');
    }
  }

  /* ------------------------------------------------------- sticky buy */

  class NmpStickyBuy extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      var target = document.getElementById(this.getAttribute('data-watch'));
      if (!target || !('IntersectionObserver' in window)) return;
      this._ready = true;

      var self = this;
      this._observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            /* Only once the button has gone UP off the screen. Before the
               shopper has scrolled to it at all it is below the fold and also
               not intersecting, and a bar that greets you on arrival is an
               advert, not a shortcut. */
            var scrolledPast = entry.boundingClientRect.top < 0;
            if (!entry.isIntersecting && scrolledPast) self.setAttribute('data-visible', '');
            else self.removeAttribute('data-visible');
          });
        },
        { threshold: 0 }
      );
      this._observer.observe(target);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }
  }

  /* One guard per name — see the note in nmp.js. Three defines behind a single
     get() of the first name is correct only while all three always ship
     together, and a double define throws rather than warning. */
  if (!window.customElements.get('nmp-variants')) {
    window.customElements.define('nmp-variants', NmpVariants);
  }
  if (!window.customElements.get('nmp-gallery')) {
    window.customElements.define('nmp-gallery', NmpGallery);
  }
  if (!window.customElements.get('nmp-sticky-buy')) {
    window.customElements.define('nmp-sticky-buy', NmpStickyBuy);
  }
})();
